import { useLocale } from '@solinkcorp/locale';
import { SimpleFormField } from '@solinkcorp/solink-ui/components/Form';
import { SelectItem } from '@solinkcorp/solink-ui/components/Select';
import { SimpleSelect } from '@solinkcorp/solink-ui/components/SimpleSelect';
import { Alert, AlertDescription } from '@solinkcorp/solink-ui/components/ui/alert';
import React from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { DurationThresholdFields } from './DurationThresholdFields';
import { type WidgetFormData } from './schema';

interface TimeWidgetSettingsProps {
  form: UseFormReturn<WidgetFormData>;
  /** Whether the widget has at least one day-part-specific threshold override set. */
  hasDayPartThresholds: boolean;
  /** Whether the customer has at least one day part defined. */
  hasAnyDayParts: boolean;
}

/**
 * Shows settings specific to time widgets.
 *
 * This is intended to be displayed as a child of `WidgetDialog`.
 */
export const TimeWidgetSettings: React.FC<TimeWidgetSettingsProps> = ({
  form,
  hasDayPartThresholds,
  hasAnyDayParts,
}) => {
  const locale = useLocale();
  const horizon = form.watch('horizon');
  const showDayPartFallbackWarning =
    horizon === 'currentDayPart' && hasAnyDayParts && !hasDayPartThresholds;
  return (
    <>
      <DurationThresholdFields form={form} />

      <SimpleFormField
        control={form.control}
        name="horizon"
        label={locale.serviceDisplayMonitor.timeHorizon}
        description={locale.serviceDisplayMonitor.chooseTimeHorizon}
        render={({ field }) => (
          <SimpleSelect className="w-[100%]" data-testid="sdm-widget-horizon-select" {...field}>
            <SelectItem value="realtime">{locale.serviceDisplayMonitor.realTime}</SelectItem>
            <SelectItem value="15m">
              {locale.serviceDisplayMonitor.averageOfLast15Minutes}
            </SelectItem>
            <SelectItem value="1h">{locale.serviceDisplayMonitor.averageOfLastHour}</SelectItem>
            <SelectItem value="currentHour">
              {locale.serviceDisplayMonitor.averageOfCurrentHour}
            </SelectItem>
            <SelectItem value="currentDay">
              {locale.serviceDisplayMonitor.averageOfCurrentDay}
            </SelectItem>
            {hasAnyDayParts && (
              <SelectItem value="currentDayPart">
                {locale.serviceDisplayMonitor.averageOfCurrentDayPart}
              </SelectItem>
            )}
          </SimpleSelect>
        )}
      />
      {showDayPartFallbackWarning && (
        <Alert variant="destructive" className="mt-2 border-border">
          <AlertDescription
            className="text-text-secondary"
            data-testid="day-part-no-association-warning"
          >
            {locale.serviceDisplayMonitor.currentDayPartNoAssociationWarning}
          </AlertDescription>
        </Alert>
      )}
    </>
  );
};
